import classNames from "classnames"
import Color from "color"
import Piece from "./Piece"
import { allPieces } from "@/utils/data"
import { getUserCreds, isMobile } from "@/utils/utils"
import { useAppSelector } from "@/store/hooks"
import React from "react"

interface PiecesProps {
  cell: ICell
}

// Colour stops the highlight layers are drawn from, handed to CSS as variables
// so App.css can own the look while the player's own piece colour drives it.
// `--pc-none` is the colour at zero alpha rather than `transparent`, which some
// engines interpolate through grey and leave a dirty ring in the gradient.
const glowVars = (color: string): React.CSSProperties =>
  ({
    "--pc": color,
    "--pc-strong": Color(color).alpha(0.55).string(),
    "--pc-soft": Color(color).alpha(0.25).string(),
    "--pc-none": Color(color).alpha(0).string(),
  } as React.CSSProperties)

const Pieces: React.FC<PiecesProps> = ({ cell }) => {
  const myGame = useAppSelector((state) => state.global.myGame)
  const turn = useAppSelector((state) => state.global.myGame?.situation?.turn)
  // The highlight is a ring of elements around the token, but the token itself
  // is what GSAP flies across the board — the rings would sit in the cell it
  // left. So they're dropped for the length of the jump and mount again on
  // landing, which is also what replays the arrival burst.
  const pieceMoving = useAppSelector(
    (state) => state.global.pieceMovementAnimationRunning
  )
  const myEmail = getUserCreds().email

  const playersInThisCell = myGame?.situation?.players
    ?.filter((player) => player.position === cell.order)
    .filter((player) => !player.isBankrupt) as IPlayer[]
  const isJustVisisitng = false
  if (isJustVisisitng) {
    const jailedPlayers = playersInThisCell?.filter((player) => player.inJail)
    const justVisitingPlayers = playersInThisCell?.filter((player) => !player.inJail)
    // const justVisitingPlayers: IPlayer[] = [
    //    playersInThisCell[0],
    //    playersInThisCell[0],
    //    playersInThisCell[0],
    //    playersInThisCell[0],
    // ];
    // const jailedPlayers: IPlayer[] = [
    //    playersInThisCell[0],
    //    playersInThisCell[0],
    //    playersInThisCell[0],
    //    playersInThisCell[0],
    // ];
    return (
      <>
        <div
          className={classNames("absolute top-[37%] right-[37%] translate-x-1/2 -translate-y-1/2 grid z-[100] ", {
            "grid-rows-1 grid-cols-1": jailedPlayers?.length === 1,
            "grid-rows-1 grid-cols-2": jailedPlayers?.length === 2,
            "grid-rows-2 grid-cols-2": jailedPlayers?.length > 2,
          })}
        >
          {jailedPlayers?.map((player) => (
            <Piece player={player}></Piece>
          ))}
        </div>
        <div
          className={classNames("absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-2  grid  z-10 ", {
            "grid-rows-1 grid-cols-1": justVisitingPlayers?.length === 1,
            "grid-rows-1 grid-cols-2": justVisitingPlayers?.length === 2,
          })}
        >
          {justVisitingPlayers?.slice(0, 2)?.map((player) => (
            <Piece player={player}></Piece>
          ))}
        </div>

        <div
          className={classNames("absolute bottom-1/2 left-0 gap-4 translate-y-2/2 -translate-x-2  grid  z-10 ", {
            "grid-rows-1 grid-cols-1": justVisitingPlayers?.length === 3,
            "grid-rows-1 grid-cols-2": justVisitingPlayers?.length === 4,
          })}
        >
          {justVisitingPlayers?.slice(2, 4)?.map((player) => (
            <Piece player={player}></Piece>
          ))}
        </div>
      </>
    )
  }
  return (
    <div
      className={classNames(
        "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid  z-20  w-full items-center justify-center",
        {
          "grid-rows-1 grid-cols-1": playersInThisCell?.length === 1,
          "grid-rows-1 grid-cols-2": playersInThisCell?.length === 2,
          "grid-rows-2 grid-cols-2": (playersInThisCell?.length ?? 0) > 2,
        }
      )}
    >
      {playersInThisCell
        ?.filter((player) => !player.isBankrupt)
        ?.map((player) => {
          const myPieceId = player?.pieceId
          const myPeice = allPieces.find((piece) => piece.id === myPieceId)
          const isActive = player.id === turn
          const isMe = player.id === myEmail
          // Rings only while the token is standing still — see the note on
          // pieceMoving above.
          const showTurnFx = isActive && !pieceMoving
          return (
            <span
              // Seat, not piece: two players can end up on the same token now
              // that pieces are picked rather than dealt, and a duplicate key
              // would collapse them into one.
              key={player.order}
              style={glowVars(myPeice?.color || "#ffffff")}
              className={classNames("piece-wrap justify-self-center z-20", {
                // Whoever is playing sits on top of everyone sharing the cell,
                // and carries the size multiplier the halo is measured from.
                "piece-wrap-active z-30": isActive,
              })}
            >
              {(showTurnFx || isMe) && (
                <span
                  // Hidden alongside the token by the swap-positions animation.
                  id={`piece-fx-${player.order}`}
                  className="piece-fx"
                  aria-hidden="true"
                >
                  {isMe && <span className="piece-fx-layer piece-you-ring" />}
                  {showTurnFx && (
                    <>
                      <span className="piece-fx-layer piece-pedestal" />
                      <span className="piece-fx-layer piece-ping" />
                      <span className="piece-fx-layer piece-ping piece-ping-late" />
                      <span className="piece-fx-layer piece-shockwave" />
                    </>
                  )}
                </span>
              )}
              {/* @ts-ignore */}
              <img
                id={`piece-${player.order}`}
                className={classNames("piece relative z-[1]", {
                  jailed: player.inJail,
                  "piece-active": isActive,
                  "w-14 h-14": isMobile(),
                  "w-10 h-10": isMobile() && playersInThisCell?.length > 1,
                  // "brightness-200": true,
                })}
                src={myPeice!.imgSrc || ""}
                // Seat order, not piece id — see the note in Piece.tsx.
                data-testid={`game.piece-${player.order}`}
              ></img>
            </span>
          )
        })}
    </div>
  )
}

export default React.memo(Pieces)
